import * as React from "react";

import { type DataTableState } from "./useTable";

/**
 * What a sortable column sorts by. Strings compare by locale, ignoring case;
 * numbers and dates compare by value. An absent value sorts last either way.
 */
export type InMemorySortKey<TData> = (
  row: TData,
) => string | number | Date | null | undefined;

/** The table state the rows are sorted and paged by. */
export type InMemoryTableState<TData> = Pick<
  DataTableState<TData>,
  "sort" | "currentPage" | "pageSize"
>;

export interface InMemoryRows<TData> {
  /** The current page's rows, sorted. */
  visibleRows: TData[];
  totalRowCount: number;
  hasNextPage: boolean;
}

function compareValues(
  a: string | number | Date,
  b: string | number | Date,
): number {
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  }
  return Number(a) - Number(b);
}

/** `rows` sorted by the table's sort column and cut to its current page. */
export function sortAndPageRows<TData>(
  table: InMemoryTableState<TData>,
  rows: readonly TData[],
  sortKeys: Readonly<Record<string, InMemorySortKey<TData>>>,
): InMemoryRows<TData> {
  const { sort, currentPage, pageSize } = table;
  const key = sort === null ? undefined : sortKeys[sort.columnId];
  let sorted: readonly TData[] = rows;
  if (sort !== null && key !== undefined) {
    const factor = sort.direction === "asc" ? 1 : -1;
    sorted = [...rows].sort((rowA, rowB) => {
      const a = key(rowA);
      const b = key(rowB);
      if (a === null || a === undefined) {
        return b === null || b === undefined ? 0 : 1;
      }
      if (b === null || b === undefined) {
        return -1;
      }
      return compareValues(a, b) * factor;
    });
  }
  const start = currentPage * pageSize;
  const end = start + pageSize;
  return {
    visibleRows: sorted.slice(start, end),
    totalRowCount: sorted.length,
    hasNextPage: end < sorted.length,
  };
}

/**
 * Sorts and pages a list fetched whole, for a `<DataTable>` whose endpoint
 * can't: pass the result's fields to the table as `data`, `totalRowCount` and
 * `hasNextPage`. A sortable column with no entry in `sortKeys` leaves the
 * rows in the order they came. When the list shrinks so that the table's
 * page is past its end, the table goes back to the first page.
 *
 * For bounded lists only: ones whose size tracks an org's configuration
 * (agents, ingestion rules), not its use. A list that grows with every run,
 * upload or chat is sorted and paged by the server through a query
 * endpoint instead, or the table answers from whatever slice the browser
 * happens to hold. `rows` must be the whole list, every page of the endpoint
 * drained, for the same reason. See `docs/guides/list-pages.md`.
 */
export function useInMemoryRows<TData>(
  table: InMemoryTableState<TData> &
    Pick<DataTableState<TData>, "resetCurrentPage">,
  rows: readonly TData[],
  sortKeys: Readonly<Record<string, InMemorySortKey<TData>>>,
): InMemoryRows<TData> {
  const { sort, currentPage, pageSize } = table;
  useFirstPageWhenPastEnd(table, rows.length);
  return React.useMemo(
    () => sortAndPageRows({ sort, currentPage, pageSize }, rows, sortKeys),
    [sort, currentPage, pageSize, rows, sortKeys],
  );
}

/**
 * Sends the table back to its first page when its `rowCount` rows, paged in
 * the browser, no longer reach the current one.
 */
export function useFirstPageWhenPastEnd(
  table: Pick<
    DataTableState<unknown>,
    "currentPage" | "pageSize" | "resetCurrentPage"
  >,
  rowCount: number,
): void {
  const { currentPage, pageSize, resetCurrentPage } = table;
  const pastTheEnd = currentPage > 0 && currentPage * pageSize >= rowCount;
  React.useEffect(() => {
    if (pastTheEnd) {
      resetCurrentPage();
    }
  }, [pastTheEnd, resetCurrentPage]);
}
